import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NodeSelect } from "@/components/links/LinkDialog";
import { fmtInput, parseAmount } from "@/lib/format";
import type { Condition, Definition, ModelNode, MetricNode, Op } from "@/model/types";

export function ConditionEditor({
	value,
	onChange,
	nodes,
	selfId,
}: {
	value: Definition;
	onChange: (d: Definition) => void;
	nodes: ModelNode[];
	selfId?: string;
}) {
	const others = nodes.filter((n) => n.id !== selfId);
	const setAtom = (i: number, c: Condition) => onChange({ ...value, when: value.when.map((x, j) => (j === i ? c : x)) });
	const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));

	return (
		<div className="space-y-2">
			<div className="flex items-center gap-2 text-sm">
				Happens in the first month when
				<Select value={value.combine} onValueChange={(v) => onChange({ ...value, combine: v as "all" | "any" })}>
					<SelectTrigger size="sm" className="w-24">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">all</SelectItem>
						<SelectItem value="any">any</SelectItem>
					</SelectContent>
				</Select>
				of these hold:
			</div>
			{value.when.map((c, i) => {
				const node = byId["event" in c ? c.event : c.metric];
				const unit = node?.kind === "metric" ? (node as MetricNode).unit : "";
				return (
					<div key={i} className="flex flex-wrap items-center gap-2 rounded-md border bg-card p-2">
						<div className="min-w-48 flex-1">
							<NodeSelect
								value={"event" in c ? c.event : c.metric}
								nodes={others}
								onChange={(id) => {
									const n = byId[id];
									if (n.kind === "event") setAtom(i, { event: id, for_months: c.for_months });
									else {
										const m = n as MetricNode;
										const v = m.observed.at(-1)?.value ?? Object.values(m.range)[0]?.[1] ?? 0;
										setAtom(i, { metric: id, op: "<=", value: v, for_months: c.for_months });
									}
								}}
							/>
						</div>
						{"event" in c ? (
							<span className="text-sm text-muted-foreground">has happened</span>
						) : (
							<>
								<Select value={c.op} onValueChange={(op) => setAtom(i, { ...c, op: op as Op })}>
									<SelectTrigger size="sm" className="w-32">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="<">is under</SelectItem>
										<SelectItem value="<=">is at or under</SelectItem>
										<SelectItem value=">">is over</SelectItem>
										<SelectItem value=">=">is at or over</SelectItem>
									</SelectContent>
								</Select>
								<CommitInput
									className="h-8 w-32 text-right tabular-nums"
									inputMode="decimal"
									value={c.value}
									format={(v) => fmtInput(v, unit)}
									parse={(t) => parseAmount(t, unit)}
									onCommit={(v) => setAtom(i, { ...c, value: v })}
									aria-label="Value"
								/>
								{unit && unit !== "USD" && unit !== "%" && <span className="text-xs text-muted-foreground">{unit}</span>}
							</>
						)}
						<span className="text-sm text-muted-foreground">for</span>
						<CommitInput
							className="h-8 w-16 text-right tabular-nums"
							inputMode="numeric"
							value={c.for_months ?? 1}
							format={(v) => String(v)}
							parse={(t) => {
								const n = Math.round(Number(t));
								return Number.isFinite(n) && n >= 1 ? n : null;
							}}
							onCommit={(v) => setAtom(i, { ...c, for_months: v })}
							aria-label="Months in a row"
						/>
						<span className="text-sm text-muted-foreground">month(s) in a row</span>
						<Button
							variant="ghost"
							size="icon"
							className="ml-auto size-8"
							disabled={value.when.length === 1}
							onClick={() => onChange({ ...value, when: value.when.filter((_, j) => j !== i) })}
							aria-label="Remove condition"
						>
							<X className="size-4" />
						</Button>
					</div>
				);
			})}
			<div className="flex flex-wrap items-center gap-3">
				<Button
					variant="outline"
					size="sm"
					onClick={() => {
						const first = others.find((n) => n.kind === "metric") ?? others[0];
						if (!first) return;
						onChange({
							...value,
							when: [
								...value.when,
								first.kind === "event" ? { event: first.id } : { metric: first.id, op: "<=", value: (first as MetricNode).observed.at(-1)?.value ?? 0 },
							],
						});
					}}
				>
					<Plus className="size-4" /> Add condition
				</Button>
				<div className="flex items-center gap-2 text-sm">
					<span className="text-muted-foreground">Only check when</span>
					<div className="w-56">
						<NodeSelect
							value={value.at ?? "__none"}
							nodes={[{ id: "__none", title: "any month", icon: "calendar-check", kind: "event" } as ModelNode, ...others.filter((n) => n.kind === "event")]}
							onChange={(id) => onChange({ ...value, at: id === "__none" ? undefined : id })}
						/>
					</div>
					<span className="text-muted-foreground">happens</span>
				</div>
			</div>
		</div>
	);
}

/**
 * A number field that commits on blur or Enter, not on every keystroke (A10 / K2), so typing "36" is one undo
 * step and one simulation. Text that isn't a number reverts to the current value.
 */
function CommitInput({
	value,
	format,
	parse,
	onCommit,
	...rest
}: { value: number; format: (v: number) => string; parse: (t: string) => number | null; onCommit: (v: number) => void } & Omit<
	React.ComponentProps<typeof Input>,
	"value" | "onChange" | "defaultValue"
>) {
	return (
		<Input
			{...rest}
			key={value}
			defaultValue={format(value)}
			onFocus={(e) => e.target.select()}
			onBlur={(e) => {
				const v = parse(e.target.value);
				if (v === null || v === value) e.target.value = format(value);
				else onCommit(v);
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter") (e.target as HTMLInputElement).blur();
				if (e.key === "Escape") {
					(e.target as HTMLInputElement).value = format(value);
					(e.target as HTMLInputElement).blur();
				}
			}}
		/>
	);
}
